import { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { extractErrorMessage } from '../../../utils/errorUtils';
import { retentionSchema } from '../schemas/retentionSchema';
import { useAuth } from '../../../hooks/useAuth';

const RetentionModalForm = ({ open, onClose, onSave, retention, companies, areas, onCompanyChange }) => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [filteredAreas, setFilteredAreas] = useState([]);

  const isOwner = user?.roles?.some(r => r.roleLevel === 0 || r.roleLevel === 1 || r.roleLevel === 2);
  const effectiveCompanyId = isOwner ? '' : user?.companyId;

  const initialValues = {
    name: retention?.name || '',
    code: retention?.code || '',
    date: retention?.date ? retention.date.split('T')[0] : '',
    companyId: retention?.companyId || effectiveCompanyId || '',
    areaId: retention?.areaId || '',
    comments: retention?.comments || '',
  };

  useEffect(() => {
    if (initialValues.companyId) {
      loadAreasForCompany(initialValues.companyId);
    }
  }, [initialValues.companyId]);

  const loadAreasForCompany = async (companyId) => {
    if (onCompanyChange) {
      await onCompanyChange(companyId);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      await onSave(values);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {retention ? 'Editar Tabla de Retención' : 'Nueva Tabla de Retención'}
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={retentionSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Grid container spacing={2}>
                {isOwner && (
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      select
                      name="companyId"
                      label="Empresa *"
                      fullWidth
                      value={values.companyId}
                      onChange={(e) => {
                        setFieldValue('companyId', e.target.value);
                        setFieldValue('areaId', '');
                        loadAreasForCompany(e.target.value);
                      }}
                      error={touched.companyId && Boolean(errors.companyId)}
                      helperText={touched.companyId && errors.companyId}
                    >
                      <MenuItem value="">Seleccione una empresa</MenuItem>
                      {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name} ({company.short})
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={areas}
                    getOptionLabel={(o) => `${o.name} (${o.code})`}
                    value={areas.find(a => String(a.id) === String(values.areaId)) || null}
                    onChange={(_, val) => setFieldValue('areaId', val ? val.id : '')}
                    disabled={!values.companyId}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    noOptionsText="Sin áreas disponibles"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Área *"
                        error={touched.areaId && Boolean(errors.areaId)}
                        helperText={touched.areaId && errors.areaId}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Field
                    as={TextField}
                    name="name"
                    label="Nombre de la Tabla de Retención *"
                    fullWidth
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="code"
                    label="Código *"
                    fullWidth
                    error={touched.code && Boolean(errors.code)}
                    helperText={touched.code && errors.code}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="date"
                    label="Fecha *"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={touched.date && Boolean(errors.date)}
                    helperText={touched.date && errors.date}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="comments"
                    label="Comentarios"
                    fullWidth
                    multiline
                    rows={3}
                    error={touched.comments && Boolean(errors.comments)}
                    helperText={touched.comments && errors.comments}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default RetentionModalForm;
